import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Moment from "moment";
import { SpirometryGraphs, FvcGraphDataMapping } from "../SpirometryGraphs";
import { FvcAnalysisSet, FvcSession, FvcAnalysisSetMutation, AnalysisSetFixedComment, SessionNote } from "webportalclient";
import { HomeSpirometryStatus, OverreadStatusIdEnum } from "../../../misc/api/apiTypes";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { GraphTestData } from "../SpirometryAnalysisScreen";
import { ActiveSpirometryResultsTable } from "./ActiveSpirometryResultsTable";
import { SummaryPanel } from "../../Shared/SummaryPanel";
import { useSecurityContext } from "../../../Core/SecurityContext";
import { useAcceptabilityNextGen, UpdateAnalysisSetAction } from "../../Shared/UseAcceptabilityNextGen";
import { LeanSessionMetadata, LeanSessionMetadataTable } from "../../Shared/SessionMetadataTable";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { useGraphs } from "../../Shared/UseGraphs";
import * as UseNextGenFixedComments from "./UseNextGenFixedComments";
import { SessionTypes } from "../../../misc/api/ApiSessionTypes";
import { FixedCommentSetTypeEnum } from "../../../misc/api/FixedComments/FixedCommentSetTypes";
import { LocalizedDateTime, DateTimeAccuracy } from "../../../misc/components/LocalizedDateTime";
import { useInterval } from "../../../misc/Hooks/UseInterval";
import { useWebPortalApiClient, useWebPortalLegacyClient,  } from "../../../misc/api/WebPortalTokenClient";
import { LoadTestFixedCommentsAction, useFixedCommentsDispatch, useFixedCommentsState } from "../../../Core/FixedCommentsContext";
import { FixedCommentEntityTypeEnum } from "../../../misc/api/FixedComments/FixedCommentSetEntityTypes";
import { calculateFvcRepeatabilityTopTwoNew } from "../../Shared/RepeatabilityCalculations";
import { SpirometryAnalysisSetTypeEnum } from "../../../misc/api/ApiSpirometryAnalysisSetType";
import { GoBackToList } from "../../Shared/AnalysisHelpers";
import { PermissionEnum } from "../../../misc/api/ApiPermissionEnum";
import ReactMarkdown = require("react-markdown");
import * as remarkGfm from 'remark-gfm';
import { ErrorBoundary } from "../../../errors/ErrorBoundary";
import '../../../misc/components/Formatters/MarkdownStyles.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCompress, faExpand } from '@fortawesome/free-solid-svg-icons';
import { ToastContainer, toast } from "react-toastify";
import { useReferenceContextProvider } from "../../../Core/ReferenceContextProvider";
import { ModalSize } from '../../../misc/components/modal';
import { calculateBestValuesGrading } from "../SpirometryBestValuesGrading";

interface ExpectedMatchParams {
    analysisSetId: string;
    viewState: "view" | "edit";
}

export const ActiveSpirometrySessionView = () => {
    const { analysisSetId, viewState } = useParams();
    const navigate = useNavigate();
    const { currentUser, currentUserPermissions } = useSecurityContext();
    const { fvcSettings } = useCurrentStudyConfigurationContext();
    const [analysisSet, setAnalysisSet] = React.useState<FvcAnalysisSet>();
    const [fvcSession, setFvcSession] = React.useState<FvcSession>();
    const [graphData, setGraphData] = React.useState<GraphTestData[]>();
    const client = useWebPortalApiClient();
    const [sessionMetadata, setSessionMetadata] = React.useState<LeanSessionMetadata>()
    const [error, setError] = React.useState<string | undefined>();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const legacyClient = useWebPortalLegacyClient();
    const [isFetching, setIsFetching] = React.useState<boolean>(false);
    const [noteError, setNoteError] = React.useState<string | undefined>();
    const [graphState, graphDispatch] = useGraphs(graphData);
    const [acceptabilityState, acceptabilityDispatch] = useAcceptabilityNextGen(analysisSet);
    const [fixedCommentsState, fixedCommentsDispatch] = UseNextGenFixedComments.useNextGenFixedComments(SessionTypes.Fvc, FixedCommentSetTypeEnum.Technician, analysisSet);

    const [updatedAnalysisSet, setUpdatedAnalysisSet] = React.useState<FvcAnalysisSet>();
    const [preAnalysisSet, setPreAnalysisSet] = React.useState<FvcAnalysisSet>();

    const [instructionsCollapsed, setInstructionsCollapsed] = React.useState<boolean>(false);
    const { study } = useCurrentStudyConfigurationContext();
    const globalFixedCommentsState = useFixedCommentsState();
    const globalFixedCommentsDispatch = useFixedCommentsDispatch();
    const [sessionNotes, setSessionNotes] = React.useState<SessionNote[]>([]);
    const { countries } = useReferenceContextProvider();
    const liveStatusToastId = React.useRef(null);


    useInterval(async () => {
        if (!fvcSession || isFetching)
            return;

        if (fvcSession.status === HomeSpirometryStatus.Completed)
            return;

        if (fvcSession.tests.length >= fvcSettings.MaxTests)
            return;

        liveStatusToastId.current = toast.info("Waiting for new tests...", {
            toastId: "test-status",
            position: "top-center",
            theme: "colored",
            autoClose: false
        });

        setIsFetching(true);

        const analysisSetTests = await client.client.getSpirometryAnalysisSetTests(undefined, `analysisSetId eq ${analysisSetId}`, undefined, undefined, undefined, 0, undefined, true);
        if (acceptabilityState.analysisSet.tests.length === analysisSetTests["@odata.count"]) {
            setIsFetching(false);
            return;
        }

        toast.update(liveStatusToastId.current, {
            type: "success",
            render: () => "Imported new test",
            toastId: "test-status",
            position: "top-center",
            theme: "colored",
            autoClose: 5000
        });

        const siteAnalysisSetResult = await fetchSiteAnalysisSet();
        setUpdatedAnalysisSet(siteAnalysisSetResult);

        const session = await fetchSession(fvcSession.id);
        setFvcSession(session);

        const graphDataMapped = FvcGraphDataMapping(session);
        setGraphData(graphDataMapped);

        acceptabilityDispatch(UpdateAnalysisSetAction.create(siteAnalysisSetResult));

        await updateFixedComments(siteAnalysisSetResult);

        setIsFetching(false);
    }, 10000);

    const updateFixedComments = async (updatedSiteAnalysisSet: FvcAnalysisSet) => {
        const commentSets = await client.client.getStudyFixedCommentSets(undefined, `studyId eq ${study.id}`, undefined, `fixedCommentSets($expand=comments)`);

        const testSets = commentSets.value.map(ss => ({
            ...ss,
            fixedCommentSets: ss.fixedCommentSets.filter(fcs => fcs.entityType === FixedCommentEntityTypeEnum.Test)
        }));

        for (const set of testSets) {
            globalFixedCommentsDispatch(LoadTestFixedCommentsAction.create(set.sessionType as SessionTypes, set.commentSetType as FixedCommentSetTypeEnum, null, set.fixedCommentSets.map(x => x.comments).flat()));
        }

        const testFixedComments = globalFixedCommentsState.test.get(`${SessionTypes.Fvc}-${FixedCommentSetTypeEnum.Technician}`);

        fixedCommentsDispatch(UseNextGenFixedComments.UpdateFixedCommentsAction.create(updatedSiteAnalysisSet, testFixedComments));
    }

    const fetchSession = async (sessionId: string) => {
        const fvcSessionResult = await client.client.getFvcSession(sessionId, undefined, "notes,tests,visit($select=name;$expand=subject($select=id,number;$expand=site($select=id,number,name,country)))");

        setSessionMetadata({
            Id: fvcSessionResult.id,
            SiteId: fvcSessionResult.visit.subject.site.id,
            SiteName: fvcSessionResult.visit.subject.site.name,
            SiteCountryCode: fvcSessionResult.visit.subject.site.country,
            SiteCountryName: countries.find(c => c.id === fvcSessionResult.visit.subject.site.country)?.name,
            SiteNumber: fvcSessionResult.visit.subject.site.number,
            Technician: fvcSessionResult.technician,
            SubjectId: fvcSessionResult.visit.subject.id,
            SubjectNumber: fvcSessionResult.visit.subject.number,
            VisitName: fvcSessionResult.visit.name,
            SessionName: fvcSessionResult.name,
            SessionDateTime: fvcSessionResult.occurred,
            SessionTypeId: fvcSessionResult.sessionType,
            ReceivedDateTime: fvcSessionResult.created,
            SessionTypeName: fvcSessionResult.sessionType,
            Status: "",
            StatusId: "",
            Overreader: "",
            AnalysisSets: [],
        });

        setSessionNotes(fvcSessionResult.notes);
        return fvcSessionResult;
    }

    const fetchSiteAnalysisSet = async () => {
        const siteAnalysisSetResult = await client.client.getSpirometryAnalysisSet(analysisSetId, undefined, "tests($expand=test)");
        return siteAnalysisSetResult;
    }

    const getData = async () => {
        const startAnalysis = async (fvcSessionId: string, analysisSet: FvcAnalysisSet) => {
            if (analysisSet.status === OverreadStatusIdEnum.NotStarted) {
                await client.client.patchFvcSession({
                    technician: currentUser.Username,
                }, fvcSessionId);

                await client.client.patchSpirometryAnalysisSet({
                    user: currentUser.Username,
                    status: OverreadStatusIdEnum.Pending,
                }, analysisSetId);
            }
        }

        const siteAnalysisSetResult = await fetchSiteAnalysisSet();

        const fvcSessionResult = await fetchSession(siteAnalysisSetResult.sessionId);

        setAnalysisSet(siteAnalysisSetResult);
        setFvcSession(fvcSessionResult);

        if (fvcSessionResult.preSessionId !== null && fvcSessionResult.preSessionId !== undefined) {
            const preAnalysisSetResult = await client.client.getSpirometryAnalysisSets(undefined, `sessionId eq ${fvcSessionResult.preSessionId} and type eq '${SpirometryAnalysisSetTypeEnum.Site}'`);
            setPreAnalysisSet(preAnalysisSetResult.value.length > 0 ? preAnalysisSetResult.value[0] : undefined);
        }

        await startAnalysis(fvcSessionResult.id, siteAnalysisSetResult);

        fixedCommentsDispatch(UseNextGenFixedComments.LoadAnalysisSetSelectionsAction.create(siteAnalysisSetResult.fixedComments, siteAnalysisSetResult.tests));

        const graphDataMapped = FvcGraphDataMapping(fvcSessionResult);
        setGraphData(graphDataMapped);
    }

    React.useEffect(() => {
        const fetchBestValues = async () => {
            if (acceptabilityState.analysisSet !== undefined && fvcSession !== undefined) {
                await calculateBestValuesGrading(legacyClient, acceptabilityState, acceptabilityDispatch, fvcSession, fvcSettings);
            }
        };


        fetchBestValues();
    }, [acceptabilityState.changedAcceptability]);

    React.useEffect(() => {
        getData();
    }, []);


    const addNewNote = async (annotation: string) => {
        try {
            await client.client.postSessionNote({
                sessionId: fvcSession.id,
                userEmailAddress: currentUser.Email,
                comment: annotation,
                recorded: new Date().toISOString()
            });
        } catch (ex) {
            setNoteError(ex.message);
            return;
        }
        await fetchSession(fvcSession.id);
    }

    const editNote = async (id: string, comment: string, annotation: string) => {
        await client.client.patchSessionNote(
            {
                comment: comment,
            }, id, annotation);
        await fetchSession(fvcSession.id);
    }

    const setAnalysisDecision = async () => {
        setIsSubmitting(true);
        setError(undefined);

        let statusValue;

        if (acceptabilityState.analysisSet.tests.filter(t => t.accepted === true).length >= fvcSettings.AcceptableTests) {

            const fvcRepeatabilityResult = await calculateFvcRepeatabilityTopTwoNew(acceptabilityState.analysisSet.tests, fvcSettings.IndividualAcceptabilitiesEnabled);

            const fvcRepeatabilityThreshold = fvcSettings.AtsDeviationsFvcVolume;
            const fev1RepeatabilityThreshold = fvcSettings.AtsDeviationsFev1Volume;

            if (fvcRepeatabilityResult.fvcAbsolute <= fvcRepeatabilityThreshold && fvcRepeatabilityResult.fev1Absolute <= fev1RepeatabilityThreshold) {
                statusValue = 'ORS_ACCEPTED';
            } else {
                statusValue = 'ORS_REJECTED';
            }
        }
        else {
            statusValue = 'ORS_REJECTED';
        }

        try {
            for (const test of acceptabilityState.analysisSet.tests) {
                await client.client.patchSpirometryAnalysisSetTest({
                    accepted: test.accepted,
                    fixedComments: fixedCommentsState.testFixedComments.find(tfc => tfc.testId === test.testId).comments.filter(c => c.selected).map(c => ({
                        analysisSetTestId: test.id,
                        fixedCommentId: c.id,
                        followOnComment: c.followOnCommentAnnotation,
                    })),
                }, test.id);
            }

            await client.client.patchFvcSession({
                status: HomeSpirometryStatus.Completed,
            }, acceptabilityState.analysisSet.sessionId);

            const response = await client.client.patchSpirometryAnalysisSet({
                ...acceptabilityState.analysisSet,
                tests: undefined,
                fixedComments: fixedCommentsState.sessionFixedComments.comments.filter(c => c.selected).map(c => ({
                    analysisSetId: acceptabilityState.analysisSet.id,
                    fixedCommentId: c.id,
                    followOnComment: c.followOnCommentAnnotation,
                } as AnalysisSetFixedComment)),
                status: statusValue
            } as FvcAnalysisSetMutation, acceptabilityState.analysisSet.id);

            if (response.id !== analysisSetId) {
                setError("An error occurred saving the analysis set. Please contact an Administrator.");
                setIsSubmitting(false);
            }

            GoBackToList(navigate, currentUserPermissions, "/sessions", PermissionEnum.AccessSingleFvcSession, "/spirometry");
        }
        catch (ex) {
            setIsSubmitting(false);
            setError(ex.Message);
            window.scrollTo(0, 0);
        }
    }

    const collapseInstructions = () => {
        setInstructionsCollapsed(!instructionsCollapsed);
    }

    if (graphState.graphData === undefined || acceptabilityState.analysisSet === undefined)
        return <LoadingWithMessage message="Loading FVC Session..." />

    const acceptanceWindowClosureTime = Moment(analysisSet.firstCompleted).add(fvcSettings.TestAcceptanceChangeTimeWindow, 'hours');
    const cannotChangeAcceptance = fvcSession.status === HomeSpirometryStatus.Completed && fvcSettings.TestAcceptanceChange && Moment().isAfter(acceptanceWindowClosureTime);

    const hasAddNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.AddFvcSessionNotes);
    const hasViewNotePermission = currentUserPermissions.some(x => x.Id == PermissionEnum.ViewFvcSessionNotes);

    return (
        <React.StrictMode>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                pauseOnHover
                theme="light"
                style={{ width: "500px" }}
            />
            <Alert show={error !== undefined} category={AlertCategoryEnum.Danger}>
                {error}
            </Alert>

            <Alert show={cannotChangeAcceptance} category={AlertCategoryEnum.Danger}>
                The session acceptability can no longer be changed, window for changes closed on <LocalizedDateTime time={acceptanceWindowClosureTime.toISOString()} accuracy={DateTimeAccuracy.Minute} />
            </Alert>

            <h2 className="text-primary">Spirometry Data</h2>
            <LeanSessionMetadataTable
                sessionMetadata={sessionMetadata}
                selectedAnalysisSetId={acceptabilityState.analysisSet.id}
            />

            {fvcSettings.HomeSpirometryInstructions ? <ErrorBoundary><h2 className="text-primary">Instructions</h2>
                <div className="panel panel-info">
                    <div className="panel-body">
                        <span className="panel-title pull-right">
                            <FontAwesomeIcon className="text-primary" icon={instructionsCollapsed ? faExpand : faCompress} style={{ cursor: "pointer" }} onClick={collapseInstructions} />
                        </span>
                        {!instructionsCollapsed ? <ReactMarkdown remarkPlugins={[remarkGfm]} className="reactMarkdown">
                            {fvcSettings.HomeSpirometryInstructions}
                        </ReactMarkdown> : <a onClick={collapseInstructions}>(Expand...)</a>}
                    </div>
                </div></ErrorBoundary> : null}

            <h2 className="text-primary">Charts</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <div className="row">
                        <div className="col-md-12">
                            <div className="btn-group pull-right">
                            </div>
                        </div>
                    </div>
                    <SpirometryGraphs data={graphState.graphData} tZeroAdjust={false} expiratoryOnlyVisible={false} />
                </div>
            </div>

            <h2 className="text-primary">Results</h2>
            <div className="panel panel-default">
                <div className="panel-body">
                    <ActiveSpirometryResultsTable
                        acceptabilityChangesAllowed={viewState === "edit" && !cannotChangeAcceptance}
                        graphData={graphState.graphData}
                        analysisSet={acceptabilityState.analysisSet}
                        preAnalysisSet={preAnalysisSet}
                        session={fvcSession}
                        acceptabilityDispatch={acceptabilityDispatch}
                        acceptabilityState={acceptabilityState}
                        fixedCommentsState={fixedCommentsState}
                        fixedCommentsDispatch={fixedCommentsDispatch}
                        graphDispatch={graphDispatch}
                    />
                </div>
            </div>
            {hasViewNotePermission ? <SummaryPanel
                notesButtonDisabled={analysisSet.type === SpirometryAnalysisSetTypeEnum.QC}
                notesButtonName="Session Notes"
                showNotesCount={true}
                isLoading={false}
                notesErrorStatus={noteError}
                isAddingNotesEnabled={viewState === "edit" && hasAddNotePermission}
                notes={sessionNotes}
                modalSize={ModalSize.Large}
                onAddNote={(annotation) => addNewNote(annotation)}
                onEditNote={(id, comment, annotation) => editNote(id, comment, annotation)}
            /> : null}
            <div className="panel panel-default" hidden={!(viewState === "edit")}>
                <div className="panel-body">
                    <div className="row">
                        <div className="col-md-12">
                            <button className="btn btn-primary" disabled={isSubmitting} onClick={setAnalysisDecision} data-automationid="CompleteButton">
                                Complete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </React.StrictMode>
    );
}
